import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getOrders } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { StatusBadge } from '../components/StatusBadge';
import { formatOrderNo, formatDate } from '../lib/utils';
import { toast } from 'sonner';
import { CalendarDays, MapPin, Clock, ChevronRight, Navigation } from 'lucide-react';
import { de } from 'date-fns/locale';

export const EmployeeCalendarPage = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const { user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        loadOrders();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadOrders = async () => {
        try {
            const res = await getOrders();
            // Filter for assigned orders (ASSIGNED and IN_PROGRESS)
            const myOrders = res.data.filter(o => 
                o.assigned_to_user_id === user?.id && 
                ['ASSIGNED', 'IN_PROGRESS'].includes(o.status)
            );
            setOrders(myOrders);
        } catch (err) {
            toast.error('Fehler beim Laden der Aufträge');
        } finally {
            setLoading(false);
        }
    };

    // Get orders for selected date
    const getOrdersForDate = (date) => {
        if (!date) return [];
        const dateStr = date.toISOString().split('T')[0];
        return orders.filter(o => {
            if (!o.planned_date) return false;
            return o.planned_date.split('T')[0] === dateStr;
        });
    };

    // Get all dates that have orders (for highlighting)
    const getDatesWithOrders = () => {
        return orders
            .filter(o => o.planned_date)
            .map(o => new Date(o.planned_date));
    };

    // Get today's and upcoming orders sorted by date
    const getUpcomingOrders = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        return orders
            .filter(o => {
                if (!o.planned_date) return true; // Include orders without date
                const orderDate = new Date(o.planned_date);
                orderDate.setHours(0, 0, 0, 0);
                return orderDate >= today;
            })
            .sort((a, b) => {
                if (!a.planned_date) return 1;
                if (!b.planned_date) return -1;
                return new Date(a.planned_date) - new Date(b.planned_date);
            });
    };

    const openGoogleMapsNavigation = (order) => {
        const address = order.service_address || order.customer?.billing_address;
        if (address) {
            // Using Google Maps directions URL for navigation
            window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}&travelmode=driving`, '_blank');
        } else {
            toast.error('Keine Adresse verfügbar');
        }
    };

    const selectedDateOrders = getOrdersForDate(selectedDate);
    const upcomingOrders = getUpcomingOrders();
    const datesWithOrders = getDatesWithOrders();

    // Memoize calendar modifiers to prevent unnecessary re-renders
    const calendarModifiers = useMemo(() => ({
        hasOrder: datesWithOrders
    }), [datesWithOrders]);
    
    const calendarModifiersClassNames = useMemo(() => ({
        hasOrder: 'day-has-order'
    }), []);

    // Custom day content to show dots for days with orders
    const isDayWithOrder = (day) => {
        const dayStr = day.toISOString().split('T')[0];
        return orders.some(o => o.planned_date && o.planned_date.split('T')[0] === dayStr);
    };

    if (loading) {
        return (
            <div className="p-6 flex items-center justify-center min-h-screen">
                <div className="spinner" />
            </div>
        );
    }

    return (
        <div className="p-4 md:p-6 space-y-6 pb-24" data-testid="employee-calendar-page">
            {/* Header */}
            <div className="mb-2">
                <h1 className="text-xl md:text-2xl font-bold">Kalender</h1>
                <p className="text-muted-foreground">Ihre geplanten Aufträge im Überblick</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Calendar */}
                <Card className="lg:col-span-1">
                    <CardContent className="p-4">
                        <Calendar
                            mode="single"
                            selected={selectedDate}
                            onSelect={setSelectedDate}
                            locale={de}
                            className="rounded-md"
                            modifiers={calendarModifiers}
                            modifiersClassNames={calendarModifiersClassNames}
                        />
                        <div className="mt-4 pt-4 border-t">
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <div className="h-4 w-4 rounded bg-primary" />
                                <span>Aufträge geplant</span>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Orders for selected date */}
                <Card className="lg:col-span-2">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <CalendarDays className="h-5 w-5" />
                            {selectedDate ? (
                                <span>
                                    {selectedDate.toLocaleDateString('de-DE', { 
                                        weekday: 'long', 
                                        day: 'numeric', 
                                        month: 'long',
                                        year: 'numeric'
                                    })}
                                </span>
                            ) : (
                                <span>Datum auswählen</span>
                            )}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {selectedDateOrders.length === 0 ? (
                            <div className="text-center py-8 text-muted-foreground">
                                <CalendarDays className="h-12 w-12 mx-auto mb-3 opacity-50" />
                                <p>Keine Aufträge für diesen Tag geplant</p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {selectedDateOrders.map((order, index) => (
                                    <div 
                                        key={order.id}
                                        className="p-4 border rounded-sm bg-card hover:border-primary/30 transition-colors"
                                        data-testid={`calendar-order-${order.order_no}`}
                                    >
                                        <div className="flex items-start justify-between mb-3">
                                            <div>
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="text-sm font-bold text-primary bg-primary/10 px-2 py-0.5 rounded">
                                                        #{index + 1}
                                                    </span>
                                                    <span className="order-no">{formatOrderNo(order.order_no)}</span>
                                                    <StatusBadge status={order.status} />
                                                </div>
                                                <h3 className="font-semibold">{order.title}</h3>
                                                <p className="text-sm text-muted-foreground">{order.customer?.name}</p>
                                            </div>
                                        </div>
                                        
                                        {(order.service_address || order.customer?.billing_address) && (
                                            <div className="flex items-start gap-2 text-sm text-muted-foreground mb-3">
                                                <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
                                                <span>{order.service_address || order.customer?.billing_address}</span>
                                            </div>
                                        )}
                                        
                                        <div className="flex gap-2">
                                            <Button 
                                                variant="outline" 
                                                size="sm"
                                                className="flex-1"
                                                onClick={() => navigate(`/my-orders/${order.id}`)}
                                                data-testid={`view-order-${order.order_no}`}
                                            >
                                                Details
                                                <ChevronRight className="h-4 w-4 ml-1" />
                                            </Button>
                                            <Button 
                                                size="sm"
                                                className="flex-1 bg-primary hover:bg-primary/90"
                                                onClick={() => openGoogleMapsNavigation(order)}
                                                data-testid={`navigate-${order.order_no}`}
                                            >
                                                <Navigation className="h-4 w-4 mr-1" />
                                                Navigation
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Upcoming Orders Timeline */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                        <Clock className="h-5 w-5" />
                        Anstehende Aufträge ({upcomingOrders.length})
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {upcomingOrders.length === 0 ? (
                        <p className="text-center py-8 text-muted-foreground">Keine anstehenden Aufträge</p>
                    ) : (
                        <div className="space-y-3">
                            {upcomingOrders.map((order, index) => (
                                <div 
                                    key={order.id}
                                    className="flex items-center gap-4 p-3 border rounded-sm hover:border-primary/30 transition-colors cursor-pointer"
                                    onClick={() => navigate(`/my-orders/${order.id}`)}
                                    data-testid={`upcoming-order-${order.order_no}`}
                                >
                                    {/* Order number in sequence */}
                                    <div className="h-10 w-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">
                                        {index + 1}
                                    </div>
                                    
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="order-no text-primary">{formatOrderNo(order.order_no)}</span>
                                            <StatusBadge status={order.status} />
                                        </div>
                                        <p className="font-medium truncate">{order.title}</p>
                                        <p className="text-sm text-muted-foreground truncate">{order.customer?.name}</p>
                                    </div>
                                    
                                    <div className="text-right shrink-0">
                                        <p className="text-sm font-medium">
                                            {order.planned_date ? formatDate(order.planned_date) : 'Kein Datum'}
                                        </p>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="mt-1 text-primary hover:text-primary/80 hover:bg-primary/10"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                openGoogleMapsNavigation(order);
                                            }}
                                            data-testid={`nav-btn-${order.order_no}`}
                                        >
                                            <Navigation className="h-4 w-4 mr-1" />
                                            Route
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default EmployeeCalendarPage;
